import React, { Fragment, PureComponent } from 'react';
import { func, object, string } from 'prop-types';
import { injectIntl } from 'react-intl';
import { Box } from '@mui/material';
import { Button } from '../../Button';
import PasswordInput from '../../../components/PasswordInput';
import { AUTH_ERROR_TYPES } from '../../../constants/Values';

const styles = {
  label: {
    whiteSpace: 'pre-line',
  },
};

/**
 * Represents stateless functional component NewPasswordForm
 * Asks user to enter and confirm new password
 * @param {object} props Component props
 * @param {function} props.confirmUserSignUp Dispatches an action of a specific type
 * @param {function} props.onFieldChange Password change callback function
 * @param {String} props.password Represents user password
 * @param {String} props.passwordRepeat Should be equal to the password
 * @param {function} props.showAuthLoader Shows form loader if form is submitted
 * @return {Component}
 * */
class NewPasswordForm extends PureComponent {
  static propTypes = {
    confirmUserSignUp: func,
    errorMessage: string,
    handleBackClick: func,
    intl: object.isRequired,
    onFieldChange: func,
    password: string,
    passwordRepeat: string,
    resetErrorMessage: func,
    showAuthLoader: func,
  };

  static defaultProps = {
    confirmUserSignUp: () => {},
    errorMessage: '',
    handleBackClick: () => {},
    onFieldChange: () => {},
    password: '',
    passwordRepeat: '',
    resetErrorMessage: () => {},
    showAuthLoader: () => {},
  };

  componentWillUnmount() {
    if (this.props.errorMessage) {
      this.props.resetErrorMessage(AUTH_ERROR_TYPES.NEW_PASS);
    }
  }

  onFormSubmit = event => {
    event.preventDefault();

    if (this.shouldDeclineToConfirmSignUp()) {
      return;
    }

    this.confirmUserSignUp();
  };

  shouldDeclineToConfirmSignUp = () =>
    !this.props.password ||
    !this.props.passwordRepeat ||
    this.props.password !== this.props.passwordRepeat;

  handleFieldChange = event => this.props.onFieldChange(event.target.id, event.target.value);

  confirmUserSignUp = () => {
    this.props.showAuthLoader();
    this.props.confirmUserSignUp();
  };

  render() {
    const { password, intl, passwordRepeat, errorMessage, handleBackClick } = this.props;

    return (
      <Fragment>
        <Box className="app-login-header" sx={{ mb: 3 }}>
          <h2>{intl.formatMessage({ id: 'auth.newPassword.header' })}</h2>
        </Box>
        <div className="app-login-form">
          <form onSubmit={this.onFormSubmit}>
            <fieldset>
              <PasswordInput
                id="password"
                defaultValue={password}
                label={intl.formatMessage({ id: 'auth.newPassword.label' })}
                margin="normal"
                autoFocus
                sx={{ mt: { xs: 1, sm: 3 }, mb: { xs: 1, sm: 3 } }}
                onChange={this.handleFieldChange}
              />
              <PasswordInput
                id="passwordRepeat"
                defaultValue={passwordRepeat}
                error={!!errorMessage}
                helperText={
                  errorMessage || intl.formatMessage({ id: 'auth.newPassword.helperText' })
                }
                label={intl.formatMessage({ id: 'auth.newPasswordRepeat.label' })}
                margin="normal"
                style={styles.label}
                sx={{ mt: { xs: 1, sm: 3 }, mb: { xs: 1, sm: 3 } }}
                onChange={this.handleFieldChange}
              />
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button
                  size="large"
                  labelId="appModule.back"
                  onClick={handleBackClick}
                  variant="text"
                />
                <Button
                  size="large"
                  labelId="auth.newPassword.buttonText"
                  color="primary"
                  disabled={this.shouldDeclineToConfirmSignUp()}
                  type="submit"
                  variant="contained"
                />
              </Box>
            </fieldset>
          </form>
        </div>
      </Fragment>
    );
  }
}

export default injectIntl(NewPasswordForm);
